import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import useBrazilianRealEstateFundIssuer from "components/company/v3/context/hooks/useBrazilianRealEstateFundIssuer";
import { CompanyFinancialsTabEnum } from "components/company/v3/menu/enums";
import FinancialsChart from "components/financials/modern/FinancialsChart";
import FinancialsComparison from "components/financials/modern/FinancialsComparison";
import FinancialsTable from "components/financials/modern/FinancialsTable";
import LocalFinancialsState from "components/financials/modern/LocalFinancialsState";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import getPlatformVerticalByIssuer, {
  overrideVerticalForDataSource,
} from "components/financials/modern/utils/platformVertical";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import { Provider } from "jotai";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { Grid2x2CheckIcon, PanelsTopLeftIcon, TrendingUpIcon } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { IFinancialsStatementType, IIndicatorDataSource, ISecurityCountry } from "types/graphqlTypes";

interface FiisFinancialsBoxProps {
  tickerSymbol: string;
  tab: CompanyFinancialsTabEnum;
  onChangeTab: (tab: CompanyFinancialsTabEnum) => void;
}

export default function FiisFinancialsBox({ tickerSymbol, tab, onChangeTab }: FiisFinancialsBoxProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  const issuer = useBrazilianRealEstateFundIssuer();

  const { value: financialsTableSettings, set: changeFinancialsTableSettings } = useChangeSetting("financialsTable", {
    sortDirection: "ASC",
    textAlignment: "CENTER",
    fullBleed: false,
  });

  const [arbitraryIndicators, setArbitraryIndicators] = useState<IIndicatorFieldsFragment[]>([]);
  const [selectedIndicators, setSelectedIndicators] = useState<IIndicatorFieldsFragment[]>([]);

  return (
    <CompanyContainer
      className={classNames("xl:h-fit xl:max-h-none", {
        "max-w-full xl:px-8": financialsTableSettings?.fullBleed,
      })}
    >
      <CompanyContentContainer>
        <LocalFinancialsState indicatorDataSource={IIndicatorDataSource.Fnet} currency="BRL">
          {(state) => (
            <Provider>
              <Tabs
                defaultValue={CompanyFinancialsTabEnum.FINANCIALS}
                value={tab}
                onValueChange={(value) => onChangeTab(value as CompanyFinancialsTabEnum)}
                className="h-full w-full"
              >
                <CompanyTabsList
                  tabs={[
                    {
                      value: CompanyFinancialsTabEnum.FINANCIALS,
                      label: intl.formatMessage({
                        defaultMessage: "Tabela",
                        description: "Aba do menu superior da empresa",
                        id: "pfZX2V",
                      }),
                      icon: <PanelsTopLeftIcon className="inline-flex" size={16} strokeWidth={2} />,
                    },
                    viewer?.isPremium
                      ? {
                          value: CompanyFinancialsTabEnum.FINANCIALS_CHART,
                          label: intl.formatMessage({
                            defaultMessage: "Gráficos",
                            description: "Aba do menu superior da empresa",
                            id: "HOiReA",
                          }),
                          icon: <TrendingUpIcon className="inline-flex" size={16} strokeWidth={2} />,
                        }
                      : null,
                    viewer?.isPremium
                      ? {
                          value: CompanyFinancialsTabEnum.COMPARE,
                          label: intl.formatMessage({
                            defaultMessage: "Comparativo",
                            description: "Aba do menu superior da empresa",
                            id: "oYMiiD",
                          }),
                          icon: <Grid2x2CheckIcon className="inline-flex" size={16} strokeWidth={2} />,
                        }
                      : null,
                  ].filter(notUndefinedOrNull)}
                />

                <CompanyTabsContent value={CompanyFinancialsTabEnum.FINANCIALS}>
                  <FinancialsTable
                    {...state}
                    country={ISecurityCountry.Br}
                    tickerSymbol={tickerSymbol}
                    canChangeDataSource={false}
                    vertical={overrideVerticalForDataSource(
                      getPlatformVerticalByIssuer(issuer),
                      state.indicatorDataSource,
                    )}
                    arbitraryIndicators={arbitraryIndicators}
                    statementType={state.statementType || IFinancialsStatementType.Annual}
                    textAlignment={financialsTableSettings?.textAlignment || "CENTER"}
                    sortDirection={financialsTableSettings?.sortDirection || "ASC"}
                    onChangeArbitraryIndicators={setArbitraryIndicators}
                    onChangeTextAlignment={(textAlignment) => {
                      changeFinancialsTableSettings((current) => ({
                        ...current,
                        textAlignment,
                      }));
                    }}
                    onChangeSortDirection={(sortDirection) => {
                      changeFinancialsTableSettings((current) => ({
                        ...current,
                        sortDirection,
                      }));
                    }}
                  />
                </CompanyTabsContent>

                <CompanyTabsContent value={CompanyFinancialsTabEnum.FINANCIALS_CHART}>
                  <FinancialsChart
                    {...state}
                    vertical={overrideVerticalForDataSource(
                      getPlatformVerticalByIssuer(issuer),
                      state.indicatorDataSource,
                    )}
                    arbitraryIndicators={arbitraryIndicators}
                    selectedIndicators={selectedIndicators}
                    tickerSymbol={tickerSymbol}
                    country={ISecurityCountry.Br}
                    statementType={state.statementType || IFinancialsStatementType.Annual}
                    onChangeSelectedIndicators={setSelectedIndicators}
                    onChangeArbitraryIndicators={setArbitraryIndicators}
                  />
                </CompanyTabsContent>

                <CompanyTabsContent value={CompanyFinancialsTabEnum.COMPARE}>
                  <FinancialsComparison
                    {...state}
                    tickerSymbol={tickerSymbol}
                    country={ISecurityCountry.Br}
                    vertical={getPlatformVerticalByIssuer(issuer)}
                    arbitraryIndicators={arbitraryIndicators}
                    statementType={state.statementType || IFinancialsStatementType.Annual}
                    selectedIssuers={state.adversaries || []}
                  />
                </CompanyTabsContent>
              </Tabs>
            </Provider>
          )}
        </LocalFinancialsState>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
